import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CircleCheck } from 'lucide-react'
import { Button } from '@/src/renderer/components/ui/Button'
import { Switch } from '@/src/renderer/components/ui/switch'
import { useBackupBridge, type OpenedBackup, type PasswordStatus } from '@/src/renderer/lib/backup/backupBridge'
import { SettingsRow, SettingsSection } from '../../primitives'
import { CreateBackupView } from './CreateBackupView'
import { RestoreBackupView } from './RestoreBackupView'
import { AutoBackupSection } from './AutoBackupSection'
import { BackupPasswordView } from './BackupPasswordView'
import type { PendingPreferencesAction } from '../../usePreferencesNavigation'
import './BackupViews.scss'
import './BackupsPage.scss'

type View = { kind: 'create' } | { kind: 'restore'; file: OpenedBackup; n: number } | { kind: 'password'; mode: 'setup' | 'change' } | null

/**
 * **Profil & Daten › Backups** (#158, ADR 0035), nur am Desktop. Eine schlanke Übersicht — je
 * Ablauf eine Zeile mit Beschreibung und Button; jeder Ablauf öffnet eine Unterseite. Darunter die
 * automatischen Backups samt Liste. Der Schalter „Backups mit Passwort schützen“ gilt für beide
 * (Entscheidung 17); Einschalten öffnet die Unterseite zum Festlegen.
 *
 * Die Übersicht bleibt während einer Unterseite stehen (nur versteckt), damit der Fokus beim Zurück
 * auf den Button findet, der sie geöffnet hat.
 */
interface BackupsPageProps {
  /** Vom Menü oder vom Doppelklick: gleich „Backup erstellen“ oder „Backup wiederherstellen“ öffnen. */
  action?: PendingPreferencesAction | null
}

export function BackupsPage({ action }: BackupsPageProps) {
  const { t } = useTranslation()
  const bridge = useBackupBridge()
  const [view, setView] = useState<View>(null)
  const [created, setCreated] = useState<string | null>(null)
  const [password, setPassword] = useState<PasswordStatus | null>(null)
  const [passwordSaved, setPasswordSaved] = useState(false)
  // Jede geöffnete Datei bekommt eine frische Unterseite: Kommt bei offener Unterseite eine zweite (Doppelklick,
  // Menü), bliebe sonst die erste stehen — die Unterseite liest ihre Datei nur beim Aufbau.
  const restoreCount = useRef(0)
  const showRestore = (file: OpenedBackup) => {
    setCreated(null)
    setView({ kind: 'restore', file, n: ++restoreCount.current })
  }

  useEffect(() => {
    void bridge.passwordStatus().then(setPassword).catch(error => console.error('[Backup] Passwort-Stand nicht lesbar:', error))
  }, [bridge])

  const onPasswordToggle = (enabled: boolean) => {
    setPasswordSaved(false)
    if (enabled) {
      setView({ kind: 'password', mode: 'setup' })
      return
    }
    void bridge.clearPassword().then(setPassword).catch(error => console.error('[Backup] Passwort nicht abgeschaltet:', error))
  }

  // Erst der Öffnen-Dialog; bricht man ihn ab, bleibt die Übersicht stehen.
  const onRestore = async () => {
    const file = await bridge.open().catch(error => {
      console.error('[Backup] Öffnen fehlgeschlagen:', error)
      return null
    })
    if (file) showRestore(file)
  }

  /*
    Menü und Doppelklick (#158, Phase D): „Backup erstellen …“ öffnet die Unterseite; „Backup
    wiederherstellen …“ nimmt erst eine per Doppelklick gemerkte Datei, sonst geht der Öffnen-Dialog auf.
  */
  // StrictMode ruft den Effekt zweimal: Der zweite Lauf holte die gemerkte Datei nicht mehr und öffnete den Dialog.
  const handledAction = useRef<number | null>(null)
  useEffect(() => {
    if (!action || handledAction.current === action.id) return
    handledAction.current = action.id
    if (action.action === 'create-backup') {
      setCreated(null)
      setView({ kind: 'create' })
    }
    if (action.action === 'restore-backup') {
      void bridge.takeOpenedFile()
        .catch(() => null)
        .then(file => {
          if (file) showRestore(file)
          else void onRestore()
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- je Aktion genau einmal
  }, [action?.id])

  return (
    <div className="backups-page">
      <div hidden={view !== null}>
        {/* Ganz oben: gilt für alles darunter — manuelle und automatische Backups. */}
        <SettingsSection title={t('preferences.backups.password.sectionTitle')} variant="card">
          <SettingsRow
            label={t('preferences.backups.password.toggle')}
            htmlFor="backups-password-enabled"
            description={(
              <>
                {t(password?.available === false ? 'preferences.backups.password.unavailable' : 'preferences.backups.password.hint')}
                <span role="status" aria-live="polite" className="backups-page__status">
                  {passwordSaved && (
                    <span className="backups-page__created">
                      <CircleCheck size={14} aria-hidden="true" />
                      {t('preferences.backups.password.saved')}
                    </span>
                  )}
                </span>
              </>
            )}
            control={(
              <div className="backups-page__password">
                {password?.enabled && (
                  <Button variant="outline" onClick={() => { setPasswordSaved(false); setView({ kind: 'password', mode: 'change' }) }}>
                    {t('preferences.backups.password.change')}
                  </Button>
                )}
                <Switch
                  id="backups-password-enabled"
                  checked={password?.enabled ?? false}
                  disabled={!password?.available}
                  onCheckedChange={onPasswordToggle}
                />
              </div>
            )}
          />
        </SettingsSection>
        <SettingsSection title={t('preferences.backups.manualTitle')} variant="card">
          <SettingsRow
            label={t('preferences.backups.createTitle')}
            description={(
              <>
                {t('preferences.backups.createDescription')}
                <span role="status" aria-live="polite" className="backups-page__status">
                  {created && (
                    <span className="backups-page__created">
                      <CircleCheck size={14} aria-hidden="true" />
                      {t('preferences.backups.created', { name: created })}
                    </span>
                  )}
                </span>
              </>
            )}
            control={(
              <Button variant="outline" onClick={() => { setCreated(null); setView({ kind: 'create' }) }}>
                {t('preferences.backups.create')}
              </Button>
            )}
          />
          <SettingsRow
            label={t('preferences.backups.restoreTitle')}
            description={t('preferences.backups.restoreDescription')}
            control={(
              <Button variant="outline" onClick={() => void onRestore()}>
                {t('preferences.backups.restore')}
              </Button>
            )}
          />
        </SettingsSection>
        <AutoBackupSection onRestore={showRestore} />
      </div>
      {view?.kind === 'create' && (
        <CreateBackupView
          onBack={() => setView(null)}
          onCreated={name => {
            setCreated(name)
            setView(null)
          }}
        />
      )}
      {view?.kind === 'restore' && <RestoreBackupView key={view.n} file={view.file} onBack={() => setView(null)} />}
      {view?.kind === 'password' && (
        <BackupPasswordView
          mode={view.mode}
          onBack={() => setView(null)}
          onSaved={status => {
            setPassword(status)
            setPasswordSaved(true)
            setView(null)
          }}
        />
      )}
    </div>
  )
}
